import { Grid, GridLocation } from '@Animation/game-objects/grid'
import { Utils } from '@Util/utils'

export type SnakePath = {
  location: GridLocation
  direction: DIRECTION
  distanceToDestination: number
}

export enum DIRECTION {
  UP = 'UP',
  DOWN = 'DOWN',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

const OPPOSITE_DIRECTION_MAP: Record<DIRECTION, DIRECTION> = {
  [DIRECTION.UP]: DIRECTION.DOWN,
  [DIRECTION.DOWN]: DIRECTION.UP,
  [DIRECTION.LEFT]: DIRECTION.RIGHT,
  [DIRECTION.RIGHT]: DIRECTION.LEFT,
}

function moveInDirection(loc: GridLocation, direction: DIRECTION) {
  let newLoc = loc
  switch (direction) {
    case DIRECTION.UP:
      newLoc = {
        row: loc.row - 1,
        col: loc.col,
      }
      break
    case DIRECTION.DOWN:
      newLoc = {
        row: loc.row + 1,
        col: loc.col,
      }
      break
    case DIRECTION.LEFT:
      newLoc = {
        row: loc.row,
        col: loc.col - 1,
      }
      break
    case DIRECTION.RIGHT:
      newLoc = {
        row: loc.row,
        col: loc.col + 1,
      }
      break
  }

  return newLoc
}

function distanceBetweenLocations(loc1: GridLocation, loc2: GridLocation, dir: DIRECTION) {
  const rowDist = Math.abs(loc1.row - loc2.row)
  const colDist = Math.abs(loc1.col - loc2.col)

  //Penalty because you cannot make a 180 degree turn
  let goingWrongWay = false

  if (dir === DIRECTION.UP && loc1.row < loc2.row && loc1.col === loc2.col) {
    goingWrongWay = true
  } else if (dir === DIRECTION.DOWN && loc1.row > loc2.row && loc1.col === loc2.col) {
    goingWrongWay = true
  } else if (dir === DIRECTION.LEFT && loc1.col < loc2.col && loc1.row === loc2.row) {
    goingWrongWay = true
  } else if (dir === DIRECTION.RIGHT && loc1.col > loc2.col && loc1.row === loc2.row) {
    goingWrongWay = true
  }

  const wrongWayPenalty = goingWrongWay ? 2 : 0

  return rowDist + colDist + wrongWayPenalty
}

function canMoveInDirection(
  grid: Grid,
  loc: GridLocation,
  target: GridLocation,
  currDirection: DIRECTION,
  newDirection: DIRECTION,
) {
  if (currDirection === OPPOSITE_DIRECTION_MAP[newDirection]) return false
  if (loc.row === target.row && loc.col === target.col) return false

  const newLocation = moveInDirection(loc, newDirection)

  const inBounds = grid.isLocationInBounds(newLocation)
  const closerToTarget =
    distanceBetweenLocations(newLocation, target, newDirection) <
    distanceBetweenLocations(loc, target, currDirection)

  return inBounds && closerToTarget
}

function moveTowardTarget(
  grid: Grid,
  currLocation: GridLocation,
  targetLocation: GridLocation,
  currDirection: DIRECTION,
) {
  const allDirections: DIRECTION[] = [DIRECTION.UP, DIRECTION.DOWN, DIRECTION.LEFT, DIRECTION.RIGHT]

  const inSameRow = currLocation.row === targetLocation.row
  const inSameCol = currLocation.col === targetLocation.col

  if (inSameCol && inSameRow) {
    return {
      direction: currDirection,
      location: currLocation,
    }
  }

  const validDirections = allDirections.filter((dir) => {
    return canMoveInDirection(grid, currLocation, targetLocation, currDirection, dir)
  })

  if (validDirections.length === 0) {
    console.error(
      `Cannot move from (${currLocation.row}, ${currLocation.col}) to (${targetLocation.row}, ${targetLocation.col}). Current direction ${currDirection}`,
    )

    throw new Error('Cannot Determine Path')
  }
  const newDirection = Utils.getRandomElementFromArray(validDirections)
  return {
    direction: newDirection,
    location: moveInDirection(currLocation, newDirection),
  }
}

function pathToTarget(grid: Grid, start: SnakePath, end: GridLocation): SnakePath[] {
  const path: SnakePath[] = [start]

  for (let i = 0; path[i].location.row !== end.row || path[i].location.col !== end.col; i++) {
    const { direction, location } = moveTowardTarget(grid, path[i].location, end, path[i].direction)
    path.push({
      location,
      direction,
      distanceToDestination: distanceBetweenLocations(location, end, direction),
    })
  }

  return path.slice(1)
}

function createPath(
  grid: Grid,
  start: GridLocation,
  end: GridLocation,
  enterFrom: GridLocation,
  steps: number,
) {
  let currDirection = DIRECTION.DOWN

  if (start.row === 0) {
    currDirection = DIRECTION.DOWN
  } else if (start.row === 7) {
    currDirection = DIRECTION.UP
  } else if (start.col === 0) {
    currDirection = DIRECTION.RIGHT
  } else if (start.col === 9) {
    currDirection = DIRECTION.LEFT
  }
  const path: SnakePath[] = [
    {
      location: start,
      direction: currDirection,
      distanceToDestination: distanceBetweenLocations(start, enterFrom, currDirection),
    },
  ]

  for (let s = 1; s < steps; s++) {
    const currPath = path[s - 1]

    //wander
    let directionArray = []

    switch (currPath.direction) {
      case DIRECTION.UP:
        directionArray = [DIRECTION.UP, DIRECTION.UP, DIRECTION.LEFT, DIRECTION.RIGHT]
        break
      case DIRECTION.DOWN:
        directionArray = [DIRECTION.DOWN, DIRECTION.DOWN, DIRECTION.LEFT, DIRECTION.RIGHT]
        break
      case DIRECTION.LEFT:
        directionArray = [DIRECTION.UP, DIRECTION.LEFT, DIRECTION.LEFT, DIRECTION.DOWN]
        break
      case DIRECTION.RIGHT:
        directionArray = [DIRECTION.UP, DIRECTION.DOWN, DIRECTION.RIGHT, DIRECTION.RIGHT]
        break
    }

    const directionOptions = directionArray.filter((dir) => {
      const newPos = moveInDirection(currPath.location, dir)
      const newDistanceTo = distanceBetweenLocations(newPos, enterFrom, dir)
      const wanderedToFar = newDistanceTo + s > steps
      const wanderToTarget = newPos.row === end.row && newPos.col === end.col && s > 3
      return grid.isLocationInBounds(newPos) && !wanderToTarget && !wanderedToFar
    })

    if (directionOptions.length === 0) {
      const newPath = pathToTarget(grid, currPath, enterFrom)
      path.push(...newPath)
      break
    } else {
      const nextDirection = Utils.getRandomElementFromArray(directionOptions)

      const nextPosition = moveInDirection(currPath.location, nextDirection)

      path.push({
        location: nextPosition,
        direction: nextDirection,
        distanceToDestination: distanceBetweenLocations(nextPosition, enterFrom, nextDirection),
      })
    }
  }

  const finalPath = pathToTarget(grid, path[path.length - 1], end)
  path.push(...finalPath)

  return path
}

export const CyberSnakesUtils = {
  createPath,
  distanceBetweenLocations,
  moveInDirection,
  moveTowardTarget,
}
